"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import finansfabrikenIcon from "@/assets/Icons/finansfabrikenLogoBeige.svg";
import mobileDropDownIcon from "@/assets/Icons/mobileDropDownIcon.svg";
import { sessionStore } from "@/utils/storage";
import {
  ABOUT_SCROLL_INTENT_KEY,
  scrollToAboutMainSection
} from "@/components/AboutUs/client/AboutUsScrollOnLoad.client";

const TABS = [
  { key: "om_oss", path: "/aboutus" },
  { key: "medarbetare", path: "/medarbetare" },
  { key: "styrelse", path: "/styrelse" },
  { key: "agare", path: "/agare" }
];

export default function AboutUsSecondaryNav() {
  const t = useTranslations();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  // Tracks whether the dropdown panel is visible OR still animating closed,
  // so the button keeps its flat bottom until the panel fully collapses.
  const [panelExpanded, setPanelExpanded] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const placeholderRef = useRef(null);
  const navWrapperRef = useRef(null);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  const getActiveTab = () => {
    const strippedPath = pathname.replace(/^\/[a-z]{2}/, "");
    if (strippedPath === "/aboutus" || strippedPath === "/aboutus/") return "om_oss";
    if (strippedPath === "/medarbetare" || strippedPath === "/medarbetare/") return "medarbetare";
    if (strippedPath === "/styrelse" || strippedPath === "/styrelse/") return "styrelse";
    if (strippedPath === "/agare" || strippedPath === "/agare/") return "agare";
    return "om_oss";
  };
  const activeTab = getActiveTab();

  const handleTabClick = () => {
    // Other tabs skip the hero and land on the main content section.
    // Tabs navigate within the same layout, so the section already exists;
    // leave an intent as a fallback in case the layout remounts.
    sessionStore.set(ABOUT_SCROLL_INTENT_KEY, "main-section");
    scrollToAboutMainSection();
  };

  // Measure the full placeholder height (including padding) for when it becomes fixed
  useEffect(() => {
    if (placeholderRef.current) {
      setPlaceholderHeight(placeholderRef.current.offsetHeight);
    }
  }, []);

  // Keep the button's flat bottom while the panel is open or still collapsing.
  // On open: flatten immediately. On close: wait for the 500ms panel
  // transition to finish before rounding the bottom corners again.
  useEffect(() => {
    if (isOpen) {
      setPanelExpanded(true);
      return;
    }
    const timeout = setTimeout(() => setPanelExpanded(false), 300);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const mainNav = document.querySelector("nav");
      if (mainNav) {
        setNavbarHeight(mainNav.offsetHeight);
      }

      if (placeholderRef.current) {
        const rect = placeholderRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= navbarHeight);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [navbarHeight]);

  return (
    <>
      {/* Placeholder — keeps space in document flow when nav becomes fixed */}
      <div
        ref={placeholderRef}
        className="w-full section-horizontal-padding about-us-section-top-padding relative z-30"
        style={{ height: isSticky ? `${placeholderHeight + 20}px` : "auto" }}
      >
        {/* Single nav wrapper that morphs between large and compact */}
        <div
          ref={navWrapperRef}
          className={`flex justify-center transition-all duration-700 ease-in-out ${
            isSticky
              ? "fixed left-0 right-0 px-[clamp(16px,6vw,100px)] pt-6 md:pt-10 2xl:pt-12 pb-2 z-[60]"
              : "relative"
          }`}
          style={isSticky ? { top: `${navbarHeight}px` } : undefined}
        >
          {/* Desktop Nav — single element that shrinks/grows */}
          <nav
            className={`hidden md:flex items-center bg-ff-purple rounded-full transition-all duration-700 ease-in-out ${
              isSticky
                ? "w-[45%] pl-4 pr-6 lg:pr-10 py-2 md:py-2.5"
                : "w-[63%] pl-6 md:pl-8 pr-6 md:pr-10 lg:pr-14 py-3.5 md:py-4"
            }`}
          >
            <Image
              src={finansfabrikenIcon}
              alt="Finansfabriken"
              width={24}
              height={24}
              className={`transition-all duration-700 ease-in-out ${
                isSticky ? "w-4 h-4 md:w-5 md:h-5" : "w-5 h-5 md:w-6 md:h-6"
              }`}
            />
            <div className="w-full flex justify-evenly">
              {TABS.map((tab) => (
                <Link
                  key={tab.key}
                  href={tab.path}
                  scroll={false}
                  onClick={() => handleTabClick()}
                  className={`cursor-pointer transition-all duration-700 ease-in-out ${
                    isSticky ? "text-[clamp(11px,0.9vw,16px)]" : "text-22px"
                  } ${
                    activeTab === tab.key
                      ? "text-[#EDD2B9] underline underline-offset-4"
                      : "text-[#EDD2B9] hover:text-[#EDD2B9]/80"
                  }`}
                >
                  {t(`aboutus.secondary_nav.${tab.key}`)}
                </Link>
              ))}
            </div>
          </nav>

          {/* Mobile Nav - Dropdown */}
          <div className="md:hidden w-full relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`flex items-center w-full bg-ff-purple px-5 transition-[padding] duration-700 ease-in-out ${
                isSticky ? "py-0.5" : "py-0.5"
              } ${panelExpanded ? "rounded-t-2xl" : "rounded-full"}`}
            >
              <Image
                src={finansfabrikenIcon}
                alt="Finansfabriken"
                width={20}
                height={20}
                className="w-5 h-5"
              />
              <span className="flex-1 text-center text-[#EDD2B9] text-[clamp(16px,4.651vw,22px)]">
                {t(`aboutus.secondary_nav.${activeTab}`)}
              </span>
              <Image
                src={mobileDropDownIcon}
                alt="dropdown"
                width={20}
                height={20}
                className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>

            <div
              className={`absolute top-full left-0 w-full bg-ff-purple rounded-b-2xl px-5 overflow-hidden transition-all duration-300 ease-in-out ${
                isOpen ? "max-h-96 pb-4 opacity-100" : "max-h-0 pb-0 opacity-0"
              }`}
            >
              <div className="flex flex-col items-center gap-3">
                {TABS.map((tab) => (
                  <Link
                    key={tab.key}
                    href={tab.path}
                    scroll={false}
                    tabIndex={isOpen ? 0 : -1}
                    onClick={() => {
                      setIsOpen(false);
                      handleTabClick();
                    }}
                    className={`cursor-pointer text-[clamp(14px,4vw,18px)] transition-all ${
                      activeTab === tab.key
                        ? "text-[#EDD2B9] underline underline-offset-4"
                        : "text-[#EDD2B9]"
                    }`}
                  >
                    {t(`aboutus.secondary_nav.${tab.key}`)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
